'use strict';

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
var moment = require('moment-timezone');
var sanitizeHtml = require('sanitize-html');
var TimeHelper = require('@knack/time-helper');
var _ = require('lodash');
var debugging = false;
var _console = console,
  log = _console.log;
var commonRuleHelper = {
  to12Hour: function to12Hour(hour24) {
    var h = Number(hour24);
    // If out of bounds, return the original value
    if (Number.isNaN(h) || h < 0 || h > 23) {
      return hour24;
    }
    return h % 12 === 0 ? 12 : h % 12;
  },
  getHours: function getHours(val) {
    var hoursAs12Hours = this.to12Hour(val.hours);
    if (val.am_pm && val.am_pm.toUpperCase() === 'AM' && hoursAs12Hours === 12) {
      return 0;
    }
    if (val.am_pm && val.am_pm.toUpperCase() === 'PM' && hoursAs12Hours !== 12) {
      return hoursAs12Hours + 12;
    }
    return val.hours;
  },
  getValString: function getValString(val, field) {
    var valString = _.isNil(val) ? '' : String(val);
    if (field.type === 'rich_text') {
      // For rich text validation, strip all tags and line breaks
      valString = sanitizeHtml(valString, {
        allowedTags: [],
        allowedAttributes: {}
      }).replace(/\n/, '');
    } else if (field.type === 'connection') {
      valString = this.getConnectionValueAsString(val);
    } else if (_.isPlainObject(val) && ['email', 'link'].includes(field.type)) {
      if (field.type === 'email') {
        valString = _.get(val, 'label', '') || _.get(val, 'email', '');
      } else if (field.type === 'link') {
        valString = _.get(val, 'url', '');
      }

      // Make sure we still have an actual string at this point.
      if (!_.isString(valString)) {
        valString = '';
      }
    } else if (_typeof(val) === 'object') {
      valString = '';
      _.each(val, function (propVal) {
        if (!_.isNil(propVal)) {
          valString += " ".concat(propVal);
        }
      });
    }
    valString = valString.toLowerCase();
    valString = valString.trim();
    return valString;
  },
  /**
   * Returns a moment version of a given date
   * @param {*} val
   * @param {*} field
   * @param {*} originalDateFormat
   * @param {*} timezone
   * @returns A moment object, undefined, or the original value
   */
  getMomentVal: function getMomentVal(val, field, originalDateFormat, timezone) {
    var momentVal;
    var dateFormat = originalDateFormat;
    if (typeof val !== 'undefined' && val !== null) {
      if (moment.isMoment(val)) {
        // If already a moment, keep the date as a moment
        momentVal = val;
      } else if (val.date) {
        // Date/Time and Date Only
        // We use meridiem format here as moment handles am, AM, aM, Am, a, a.m., a.M., etc.
        // Date only fields get their time set to 12:00 AM
        // We store civilian hours/minutes in our entry values so we don't need any special math here
        dateFormat += ' H:mmA';

        // Need to do this line in case time is formatted as an integer or in case val doesn't have an hours,
        // minutes, or am_pm attribute
        var timeFormatted = !_.isNil(val.hours) && !_.isNil(val.minutes) ? "".concat(val.hours, ":").concat(val.minutes).concat(val.am_pm || '') : val.time;
        var momentValString = "".concat(val.date_formatted || val.date, " ").concat(timeFormatted);

        // We need this line for has changed, has not changed, and similar operators
        // Those operators use val.previous_value as a rule, and val will sometimes be that previous_value
        // With those operators, val.date_formatted is always in MM/DD/YYYY format, so in the case date and
        // date_formatted are equal,
        // we can safely assume the dates are in MM/DD/YYYY format
        dateFormat = val.date_formatted === val.date ? 'MM/DD/YYYY H:mmA' : dateFormat;
        momentVal = moment.tz(momentValString, dateFormat, timezone);
      } else if (val.time || !_.isNil(val.hours) && val.hours !== 'NaN' && val.hours !== '' || _.get(field, 'format.date_format') === 'Ignore Date') {
        // Time Only
        var timeFormat = 'MM/DD/YYYY H:mmA';
        var _timeFormatted = '01/01/2012 ';

        // Need to do this line in case time is formatted as an integer or in case val doesn't have an hours, minutes,
        // or am_pm attribute
        var hasHours = !_.isNil(val.hours) && !_.isNil(val.minutes) && val.hours !== 'NaN' && val.minutes !== 'NaN';
        if (hasHours) {
          _timeFormatted += "".concat(val.hours, ":").concat(val.minutes).concat(val.am_pm || '');
        } else if (!_.isNil(val.time)) {
          _timeFormatted += val.time;
        } else {
          // if we couldn't parse out any kind of time, set to null so that momentVal will be invalid
          _timeFormatted = null;
        }
        momentVal = moment.tz(_timeFormatted, timeFormat, timezone);
      } else if (val !== '') {
        // Last-ditch formatting if we don't match the above formats
        momentVal = moment.tz(val, dateFormat, timezone);

        // Handle "Ignore Date" and set time
        if (_.get(field, 'format.date_format') === 'Ignore Date') {
          momentVal.hours(this.getHours(val));
          momentVal.minutes(val.minutes);
        }
      }
    }

    // if we couldn't parse into a valid moment, treat it like a null (all rules will be false)
    if (!moment.isMoment(momentVal) || !momentVal.isValid()) {
      momentVal = null;
    }
    return momentVal;
  },
  getDateFormat: function getDateFormat(field) {
    var dateFormat = 'MM/DD/YYYY';
    if (field && field.format && field.format.date_format) {
      switch (field.format.date_format) {
        case 'dd/mm/yyyy':
          dateFormat = 'DD/MM/YYYY';
          break;
        case 'M D, yyyy':
          // we don't actually store numbers in this format, it is for display purposes only
          break;
        case 'mm/dd/yyyy':
        default:
          dateFormat = 'MM/DD/YYYY';
          break;
      }
    }
    return dateFormat;
  },
  getRuleValString: function getRuleValString(rule) {
    var _this = this;
    var ruleValTemp = '';
    if (_.isObject(rule.value)) {
      _.each(rule.value, function (val) {
        if (!_.isNil(val)) {
          ruleValTemp += ' '.concat(_this.getConnectionValueAsString(val));
        }
      });
    }

    // Return ruleValTemp if it isn't empty, otherwise we want to return rule.value
    if (ruleValTemp) {
      return String(ruleValTemp).toLowerCase().trim();
    }

    // getValString returns an empty string when rule.value is undefined, so we want to do that here too
    return String(!_.isNil(rule.value) ? rule.value : '').toLowerCase().trim();
  },
  /**
   * Generates a start and end date based on
   * @param {string} unit - The date/time unit to check against, e.g. day, month, rolling year
   * @param {number} range - A number representing how many units we want to add / subtract
   * @param {moment} now - Moment object representing the current time
   * @returns {object} - The lower and upper boundaries of the date range
   */
  generateDateBoundaries: function generateDateBoundaries(unit, range, now) {
    var rolling = unit ? unit.includes('rolling') : false;
    var duration = rolling ? unit.split(' ')[1] : unit;

    // Lower and upper boundaries for dates based on the current time, unit, and range
    var lowerBoundary;
    var upperBoundary;

    // is during the previous
    if (range < 0) {
      // Rolling Dates are calculated by adding weeks, months, or years, starting from today
      // Calendar Dates are calculated by adding weeks, months, or years, starting from the nearest unit
      // Example range the equations below would produce: 07/16/2022 23:59:59 - 07/31/2022 00:00:00
      lowerBoundary = rolling ? now.clone().add(range, duration).add(-1, 'days').endOf('day') : now.clone().startOf(duration).add(range, duration).add(-1, 'days').endOf('day');
      upperBoundary = rolling ? now.clone().startOf('day') : now.clone().startOf(duration).startOf('day');
    } else {
      // is during the next
      lowerBoundary = rolling ? now.clone().endOf('day') : now.clone().endOf(duration).endOf('day');
      upperBoundary = rolling ? now.clone().add(range, duration).add(1, 'days').startOf('day') : now.clone().endOf(duration).add(range, duration).add(1, 'days').startOf('day');
    }
    return {
      lowerBoundary: lowerBoundary,
      upperBoundary: upperBoundary
    };
  },
  /**
   * Creates a Date/Time field using the supplied values
   * @param {string} dateFormat - Desired date format
   * @param {string} timeFormat - Desired time format
   */
  createDateTimeField: function createDateTimeField() {
    var dateFormat = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'Ignore Date';
    var timeFormat = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'Ignore Time';
    return {
      format: {
        date_format: dateFormat,
        time_format: timeFormat
      },
      type: 'date_time'
    };
  },
  /**
   * Checks whether a rule's date value is empty, null, or undefined
   * We need to check this often since we want to treat an empty date as an 'is any'
   * @param {*} value - The value with the date value stored in it
   */
  isDateValueEmpty: function isDateValueEmpty(value) {
    // If value is a string or a moment, we can't determine if a date was intentionally included or not
    // so we just want to return false in those cases
    if (!value || moment.isMoment(value) || typeof value === 'string') {
      return false;
    }
    var dateVal = value.date;
    return _.isNil(dateVal) || dateVal === '';
  },
  /**
   * Checks whether a rule's time value is empty, null, or undefined
   * We need to check this often since we want to treat an empty time as an 'is any'
   * @param {*} value - The value with the time value stored in it
   */
  isTimeValueEmpty: function isTimeValueEmpty(value) {
    // If value is a string or a moment, we can't determine if a time was intentionally included or not
    // so we just want to return false in those cases
    if (!value || moment.isMoment(value) || typeof value === 'string') {
      return false;
    }
    var timeVal = value.time;
    var hoursVal = value.hours;
    return (_.isNil(timeVal) || timeVal === '') && (_.isNil(hoursVal) || hoursVal === '' || hoursVal === 'NaN');
  },
  /**
   * Checks if a value is a valid moment object
   * @param {*} value
   */
  isValidMoment: function isValidMoment(value) {
    if (!moment.isMoment(value)) {
      return false;
    }
    return value.isValid();
  },
  isEqual: function isEqual(rule, val, field, timezone) {
    var valString = this.getValString(val, field);
    var ruleValString = this.getRuleValString(rule);
    if (valString && field.type === 'multiple_choice' && typeof rule.value !== 'boolean') {
      if (String(",".concat(valString, ",")).indexOf(",".concat(ruleValString, ",")) === -1) {
        return false;
      }
    }
    if (typeof val === 'number') {
      // eslint-disable-next-line eqeqeq
      if (val != rule.value) {
        return false;
      }
    }

    // Compare equality for Date/Times
    if (field.format && (field.type === 'date_time' || field.format.date_result === 'date')) {
      // Handle empty date-only object
      // Test time with isNil instead of empty since 0 is an allowed value
      // eslint-disable-next-line max-len
      if (_.isPlainObject(val) && _.isEmpty(val.date) && _.isNil(val.hours) && _.isNil(val.minutes) && _.isNil(val.time)) {
        return false;
      }
      var dateFormat = this.getDateFormat(field);
      var momentVal = this.getMomentVal(val, field, dateFormat, timezone);
      var ruleVal = rule.value && !moment.isMoment(rule.value) ? this.getMomentVal(rule.value, field, dateFormat, timezone) : rule.value;
      if (field.format.date_format !== 'Ignore Date' && (field.format.time_format === 'Ignore Time' || this.isTimeValueEmpty(rule.value))) {
        // Date Only Check
        if (!momentVal || !ruleVal) {
          return momentVal === ruleVal;
        }
        if (this.isValidMoment(ruleVal) && this.isValidMoment(momentVal)) {
          return momentVal.isSame(ruleVal, 'day');
        }
      } else if ((field.format.date_format === 'Ignore Date' || this.isDateValueEmpty(rule.value)) && field.format.time_format !== 'Ignore Time') {
        // Time Only Check
        if (!momentVal || !ruleVal) {
          return momentVal === ruleVal;
        }
        if (this.isValidMoment(ruleVal) && this.isValidMoment(momentVal)) {
          // Setting both dates to the same date to effectively compare only time
          // We're using 01/01/2012 because that's how we store the date portion of time-only fields
          return momentVal.year(2012).dayOfYear(1).isSame(ruleVal.year(2012).dayOfYear(1));
        }
      } else if (field.format.date_format !== 'Ignore Date' && field.format.time_format !== 'Ignore Time') {
        // Date/Time Check
        if (!momentVal || !ruleVal) {
          return momentVal === ruleVal;
        }
        if (this.isValidMoment(ruleVal) && this.isValidMoment(momentVal)) {
          return momentVal.isSame(ruleVal);
        }
      }
      return false;
    }

    /*
      When field.type === 'phone, rule.value (when not equal to a primitive) may be structured as follows:
       rule.value = {
        area: '999',
        country: null,
        ext: null,
        formatted: '(999) 999-9999',
        full: '9999999999',
        number: '9999999',
      }
       Note that rule.previousValue (structured same as above) becomes rule.value in any hasChanged method.
    */
    if (field.type === 'phone' && (_.get(rule, 'value.formatted') === val || rule.value === val)) {
      return true;
    }
    if (field.type === 'timer') {
      var _valAsNumber = this.extractNumericValue(val, field);
      return rule.value !== '' && _valAsNumber === Number(rule.value);
    }
    var valAsNumber = Number(valString);
    var ruleValAsNumber = Number(ruleValString);
    if (Number.isFinite(valAsNumber) && Number.isFinite(ruleValAsNumber)) {
      if (valAsNumber !== ruleValAsNumber) {
        return false;
      }
    } else if (valString !== ruleValString) {
      return false;
    }
    return true;
  },
  hasChanged: function hasChanged(rule, val, field, timezone) {
    var tempRule = {
      value: rule.previousValue
    };
    return !this.isEqual(tempRule, val, field, timezone);
  },
  hasNotChanged: function hasNotChanged(rule, val, field, timezone) {
    var tempRule = {
      value: rule.previousValue
    };
    return this.isEqual(tempRule, val, field, timezone);
  },
  hasChangedFrom: function hasChangedFrom(rule, val, field, timezone) {
    if (this.hasNotChanged(rule, val, field, timezone)) {
      return false;
    }
    return this.isEqual(rule, rule.previousValue, field, timezone);
  },
  hasChangedTo: function hasChangedTo(rule, val, field, timezone) {
    if (this.hasNotChanged(rule, val, field, timezone)) {
      return false;
    }
    return this.isEqual(rule, val, field, timezone);
  },
  hasChangedButNotFrom: function hasChangedButNotFrom(rule, val, field, timezone) {
    if (this.hasNotChanged(rule, val, field, timezone)) {
      return false;
    }
    return !this.isEqual(rule, rule.previousValue, field, timezone);
  },
  hasChangedButNotTo: function hasChangedButNotTo(rule, val, field, timezone) {
    if (this.hasNotChanged(rule, val, field, timezone)) {
      return false;
    }
    return !this.isEqual(rule, val, field, timezone);
  },
  isBefore: function isBefore(rule, val, field, timezone) {
    // Handle empty date-only object
    // Test time with isNil instead of empty since 0 is an allowed value
    // eslint-disable-next-line max-len
    if (_.isPlainObject(val) && _.isEmpty(val.date) && _.isNil(val.hours) && _.isNil(val.minutes) && _.isNil(val.time)) {
      return false;
    }
    var dateFormat = this.getDateFormat(field);
    var momentVal = this.getMomentVal(val, field, dateFormat, timezone);
    var ruleVal = rule.value && !moment.isMoment(rule.value) ? this.getMomentVal(rule.value, field, dateFormat, timezone) : rule.value;

    // If we don't have a valid moment object, then we can't compare our values together
    if (!this.isValidMoment(momentVal) || !this.isValidMoment(ruleVal)) {
      return false;
    }
    if (field.format && (field.type === 'date_time' || field.format.date_result === 'date')) {
      // Date Only Check
      if (field.format.date_format !== 'Ignore Date' && (field.format.time_format === 'Ignore Time' || this.isTimeValueEmpty(rule.value))) {
        return momentVal.startOf('day').isBefore(ruleVal.startOf('day'));
      }

      // Time Only Check
      if ((field.format.date_format === 'Ignore Date' || this.isDateValueEmpty(rule.value)) && field.format.time_format !== 'Ignore Time') {
        // Setting both dates to the same date to effectively compare only time
        // We're using 01/01/2012 because that's how we store the date portion of time-only fields
        return momentVal.year(2012).dayOfYear(1).isBefore(ruleVal.year(2012).dayOfYear(1), 'minute');
      }

      // Date/Time Check
      if (field.format.date_format !== 'Ignore Date' && field.format.time_format !== 'Ignore Time') {
        return momentVal.isBefore(ruleVal, 'minute');
      }
    }
    return false;
  },
  isAfter: function isAfter(rule, val, field, timezone) {
    // Handle empty date-only object
    // Test time with isNil instead of empty since 0 is an allowed value
    // eslint-disable-next-line max-len
    if (_.isPlainObject(val) && _.isEmpty(val.date) && _.isNil(val.hours) && _.isNil(val.minutes) && _.isNil(val.time)) {
      return false;
    }
    var dateFormat = this.getDateFormat(field);
    var momentVal = this.getMomentVal(val, field, dateFormat, timezone);
    var ruleVal = rule.value && !moment.isMoment(rule.value) ? this.getMomentVal(rule.value, field, dateFormat, timezone) : rule.value;

    // If we don't have a valid moment object, then we can't compare our values together
    if (!this.isValidMoment(momentVal) || !this.isValidMoment(ruleVal)) {
      return false;
    }
    if (field.format && (field.type === 'date_time' || field.format.date_result === 'date')) {
      // Date Only Check
      if (field.format.date_format !== 'Ignore Date' && (field.format.time_format === 'Ignore Time' || this.isTimeValueEmpty(rule.value))) {
        return momentVal.endOf('day').isAfter(ruleVal.endOf('day'));
      }

      // Time Only Check
      if ((field.format.date_format === 'Ignore Date' || this.isDateValueEmpty(rule.value)) && field.format.time_format !== 'Ignore Time') {
        // Setting both dates to the same date to effectively compare only time
        // We're using 01/01/2012 because that's how we store the date portion of time-only fields
        return momentVal.year(2012).dayOfYear(1).isAfter(ruleVal.year(2012).dayOfYear(1), 'minute');
      }

      // Date/Time Check
      if (field.format.date_format !== 'Ignore Date' && field.format.time_format !== 'Ignore Time') {
        return momentVal.isAfter(ruleVal, 'minute');
      }
    }
    return false;
  },
  checkRule: function checkRule(rule, val) {
    var _application$settings,
      _this2 = this;
    var field = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var application = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var valString = this.getValString(val, field);
    var valRawString = _.isNil(val) ? '' : String(val);
    var ruleValString = this.getRuleValString(rule);
    var fieldType = _.get(field, 'type');
    var timezone = TimeHelper.getTimezoneProper(application === null || application === void 0 || (_application$settings = application.settings) === null || _application$settings === void 0 ? void 0 : _application$settings.timezone);
    var momentToday = moment.tz(timezone);

    // using undefined in the checks here because date fields with a value=0 still need to be checked against criteria
    // e.g. is before
    var momentVal;
    var dateFormat = 'MM/DD/YYYY';
    if (field && field.format && field.format.date_format) {
      switch (field.format.date_format) {
        case 'dd/mm/yyyy':
          dateFormat = 'DD/MM/YYYY';
          break;
        case 'M D, yyyy':
          // we don't actually store numbers in this format, it is for display purposes only
          break;
        case 'mm/dd/yyyy':
        default:
          dateFormat = 'MM/DD/YYYY';
          break;
      }
    }
    if (typeof val !== 'undefined' && val !== null) {
      if (val.date && !moment.isMoment(val)) {
        // We use meridiem format here as moment handles am, AM, aM, Am, a, a.m., a.M., etc.
        // Date only fields get their time set to 12:00 AM
        // We store civilian hours/minutes in our entry values so we don't need any special math here
        dateFormat += ' HH:mm A';
        var momentValString = "".concat(val.date_formatted || val.date, " ").concat(val.hours, ":").concat(val.minutes, " ").concat(val.am_pm);
        momentVal = moment.tz(momentValString, dateFormat, timezone);
      } else if (moment.isMoment(val)) {
        // this is the case when a valid moment object has been passed in. retain it without parsing.
        momentVal = val;
      } else if (val !== '') {
        // not sure if the val will be correct here if the user has selected euro dates
        momentVal = moment.tz(val, dateFormat, timezone);

        // Handle "Ignore Date" and set time
        if (_.get(field, 'format.date_format') === 'Ignore Date') {
          momentVal.hours(this.getHours(val));
          momentVal.minutes(val.minutes);
        }
      }

      // if we couldn't parse into a valid moment, treat it like a null (all rules will be false)
      if (!moment.isMoment(momentVal) || !momentVal.isValid()) {
        momentVal = null;
      }
    }
    var pass = true;
    if (debugging) {
      log("check vals for rule.field: ".concat(rule.field, " val is: ").concat(val, " rule.value: ").concat(rule.value, ", rule.operator: ").concat(rule.operator, ", moment_val: ").concat(momentVal ? momentVal.format() : 'no'));
    }
    // seems like checkbox values get an extra leading space, make sure those get removed prior to checking rules
    // TODO move this out of the rule-helper
    if (typeof val === 'string') {
      // eslint-disable-next-line no-param-reassign
      val = val.trim();
    }
    switch (rule.operator) {
      // multiple choices can have more than option that are delimited by commas, so these are checked by index
      // numbers are checked otherwise '1' would not be equal to '1.0'
      // all else are simple string comparisons
      case 'is':
        pass = this.isEqual(rule, val, field, timezone);
        break;
      case 'is not':
        pass = !this.isEqual(rule, val, field, timezone);
        break;
      case 'is blank':
        switch (fieldType) {
          case 'date_time':
            if (field.format && field.format.date_format !== 'Ignore Date') {
              if (val && val !== ' ' && typeof val.date !== 'undefined' && val.date || val && val !== ' ' && typeof val.date === 'undefined') {
                pass = false;
              }
            } else if (val && val !== ' ' && val.hours) {
              pass = false;
            }
            break;
          case 'email':
            if (val && val.email || valString.length) {
              pass = false;
            }
            break;
          case 'address':
            // eslint-disable-next-line max-len
            if (val && (val.street || val.street2 || val.city || val.zip || val.state || val.latitude && val.longitude)) {
              pass = false;
            }
            break;
          case 'name':
            if (val && (val.first || val.last)) {
              pass = false;
            }
            break;
          case 'link':
            if (val && val.url || valString.length) {
              pass = false;
            }
            break;
          case 'connection':
            if (this.getConnectionValueAsString(val)) {
              pass = false;
            }
            break;
          default:
            if (valString && valString.length) {
              pass = false;
            }
            break;
        }
        break;
      case 'is not blank':
      case 'is any':
        switch (fieldType) {
          case 'date_time':
            if (field.format && field.format.date_format !== 'Ignore Date') {
              if (!val || val === ' ' || typeof val.date !== 'undefined' && !val.date || typeof val.date === 'undefined' && !val) {
                pass = false;
              }
            } else if (!val || !val.hours) {
              pass = false;
            }
            break;
          case 'email':
            if (!valString) {
              pass = false;
            }
            break;
          case 'link':
            if (!valString) {
              pass = false;
            }
            break;
          case 'address':
            // eslint-disable-next-line max-len
            if (!val || !val.street && !val.street2 && !val.city && !val.zip && !val.state && !val.latitude && !val.longitude) {
              pass = false;
            }
            break;
          case 'name':
            if (!val || !val.first && !val.last) {
              pass = false;
            }
            break;
          case 'connection':
            if (!this.getConnectionValueAsString(val)) {
              pass = false;
            }
            break;
          default:
            if (!valString || !valString.length) {
              pass = false;
            }
            break;
        }
        break;
      case 'has changed':
        pass = this.hasChanged(rule, val, field, timezone);
        break;
      case 'has not changed':
        pass = this.hasNotChanged(rule, val, field, timezone);
        break;
      case 'has changed from':
        pass = this.hasChangedFrom(rule, val, field, timezone);
        break;
      case 'has changed to':
        pass = this.hasChangedTo(rule, val, field, timezone);
        break;
      case 'has changed but not from':
        pass = this.hasChangedButNotFrom(rule, val, field, timezone);
        break;
      case 'has changed but not to':
        pass = this.hasChangedButNotTo(rule, val, field, timezone);
        break;
      case 'contains':
        // For arrays we're assuming contains is intended as "one of the options in the array", rather than a
        // part of the string. also increases fidelity since ['womens'].indexOf('mens') is -1 whereas
        // 'womens'.indexOf('mens') is not.
        // renderer passes in multi-choice checkboxes as arrays
        if (Array.isArray(val)) {
          // convert to lower case for comparison
          var lowercaseValues = val.map(function (option) {
            if (field.type === 'connection') {
              return _this2.getConnectionValueAsString(option);
            }
            return option ? String(option).toLowerCase() : '';
          });
          if (lowercaseValues.indexOf(ruleValString) === -1) {
            pass = false;
          }

          // strings
        } else if (!valString || valString.indexOf(ruleValString) === -1) {
          pass = false;
        }
        break;
      case 'does not contain':
        // should use arrays, see above
        if (val && _.isArray(val)) {
          // convert to lower case for comparison
          var _lowercaseValues = _.map(val, function (option) {
            if (!option) {
              return '';
            }
            if (typeof option === 'string' || option instanceof String) {
              return option.toLowerCase();
            }
            if (field.type === 'connection') {
              return _this2.getConnectionValueAsString(option);
            }
            var valueError = new Error("Unexpected option type: ".concat(_typeof(option), " toString value: ").concat(option.toString(), " rule was: ").concat(JSON.stringify(rule)));
            if (_.has(global, 'logger.error')) {
              global.logger.error(valueError);
            } else {
              log(valueError);
            }
            return '';
          });
          if (_lowercaseValues.indexOf(ruleValString) !== -1) {
            pass = false;
          }
        } else if (valString.indexOf(ruleValString) !== -1) {
          pass = false;
        }
        break;
      case 'starts with':
        if (!valString || valString.indexOf(ruleValString) !== 0) {
          pass = false;
        }
        break;
      case 'ends with':
        if (!valString || valString.indexOf(ruleValString, valString.length - ruleValString.length) === -1) {
          pass = false;
        }
        break;
      case 'domain must equal':
        if (!valString || valString.indexOf("@".concat(ruleValString)) > -1) {
          pass = false;
        }
        break;
      case 'domain must not equal':
        if (!valString || valString.indexOf("@".concat(ruleValString)) === -1) {
          pass = false;
        }
        break;
      case 'matches regular expression':
        try {
          if (!valRawString || !new RegExp(rule.value).test(valRawString)) {
            pass = false;
          }
        } catch (matchesRegularExpressionError) {
          // The regular expression is invalid
          // so there is no way that the value can ever match
          pass = false;
        }
        break;
      case 'does not match regular expression':
        try {
          if (!valRawString || new RegExp(rule.value).test(valRawString)) {
            pass = false;
          }
        } catch (doesNotMatchRegularExpressionError) {
          // The regular expression is invalid
          // so this will always be true
          // There's nothing else we can do here
        }
        break;

      // string
      case 'character count is':
        if (valString.length !== Number(rule.value)) {
          pass = false;
        }
        break;
      case 'character count is not':
        if (valString.length === Number(rule.value)) {
          pass = false;
        }
        break;
      case 'character count is higher than':
        if (valString.length <= Number(rule.value)) {
          pass = false;
        }
        break;
      case 'character count is lower than':
        if (valString.length >= Number(rule.value)) {
          pass = false;
        }
        break;

      // number
      case 'higher than':
        {
          var valAsNumber = this.extractNumericValue(val, field);
          if (!Number.isFinite(valAsNumber)) {
            pass = false;
          } else if (valAsNumber <= this.extractNumericValue(rule.value)) {
            pass = false;
          }
          break;
        }
      case 'lower than':
        {
          var _valAsNumber2 = this.extractNumericValue(val, field);

          // Technically, valAsNumber could be NaN here and this is NOT going to yield pass=false
          // as it TECHNICALLY should... so the condition is going to pass (e.g., if SUM < 0 is going to yield
          // true if valAsNumber is NaN)
          // However, from a UX perspective this could be perceived (and already used in production) to mean 0/nothing
          // so changing this to check for isNaN like we do in "higher than" is going to require usage analysis and
          // dev discussion
          if (_valAsNumber2 >= this.extractNumericValue(rule.value)) {
            pass = false;
          }
          break;
        }
      case 'is one of':
        if (!valString || rule.value.replace(/ /g, '').indexOf(valString) === -1) {
          pass = false;
        }
        break;
      case 'is not one of':
        if (!valString || rule.value.replace(/ /g, '').indexOf(valString) > -1) {
          pass = false;
        }
        break;

      // date
      case 'is before':
        pass = this.isBefore(rule, val, field, timezone);
        break;
      case 'is after':
        pass = this.isAfter(rule, val, field, timezone);
        break;
      case 'is today':
        if (!momentVal || !momentVal.isSame(momentToday, 'day')) {
          pass = false;
        }
        break;
      case 'is before today':
        momentToday = momentToday.startOf('day').subtract('second', 1);
      // fallthrough

      case 'is today or before':
        if (momentVal) {
          momentVal = momentVal.endOf('day');
        }
        if (!momentVal || momentVal.isAfter(momentToday, 'day')) {
          pass = false;
        }
        break;
      case 'is before current time':
        pass = this.isBefore({
          value: momentToday
        }, val, field, timezone);
        break;
      case 'is after today':
        momentToday = momentToday.endOf('day').add('second', 1);
      // fallthrough

      case 'is today or after':
        if (momentVal) {
          momentVal = momentVal.endOf('day');
        }
        if (!momentVal || momentVal.isBefore(momentToday, 'day')) {
          pass = false;
        }
        break;
      case 'is after current time':
        pass = this.isAfter({
          value: momentToday
        }, val, field, timezone);
        break;
      case 'is year to date':
        if (!momentVal || !momentVal.same(momentToday, 'year')) {
          pass = false;
        }
        break;
      case 'is during the last':
      case 'is during the previous':
        {
          var _this$generateDateBou = this.generateDateBoundaries(rule.type, rule.range * -1, momentToday),
            lowerBoundary = _this$generateDateBou.lowerBoundary,
            upperBoundary = _this$generateDateBou.upperBoundary;

          // We're creating a temporary date only field so we can effectively ignore time for these checks
          var tempField = this.createDateTimeField(field.format.date_format);

          // If lowerBoundary < val < upperBoundary, then we're 'during' the range
          pass = this.isAfter({
            value: lowerBoundary
          }, val, tempField, timezone) && this.isBefore({
            value: upperBoundary
          }, val, tempField, timezone);
          break;
        }
      case 'is before the previous':
        {
          var _this$generateDateBou2 = this.generateDateBoundaries(rule.type, rule.range * -1, momentToday),
            _lowerBoundary = _this$generateDateBou2.lowerBoundary;

          // We're creating a temporary date only field so we can effectively ignore time for these checks
          var _tempField = this.createDateTimeField(field.format.date_format);
          pass = this.isBefore({
            value: _lowerBoundary.add(1, 'days')
          }, val, _tempField, timezone);
          break;
        }
      case 'is during the next':
        {
          var _this$generateDateBou3 = this.generateDateBoundaries(rule.type, rule.range, momentToday),
            _lowerBoundary2 = _this$generateDateBou3.lowerBoundary,
            _upperBoundary = _this$generateDateBou3.upperBoundary;

          // We're creating a temporary date only field so we can effectively ignore time for these checks
          var _tempField2 = this.createDateTimeField(field.format.date_format);

          // If lowerBoundary < val < upperBoundary, then we're 'during' the range
          pass = this.isAfter({
            value: _lowerBoundary2
          }, val, _tempField2, timezone) && this.isBefore({
            value: _upperBoundary
          }, val, _tempField2, timezone);
          break;
        }
      case 'is after the next':
        {
          var _this$generateDateBou4 = this.generateDateBoundaries(rule.type, rule.range, momentToday),
            _upperBoundary2 = _this$generateDateBou4.upperBoundary;

          // We're creating a temporary date only field so we can effectively ignore time for these checks
          var _tempField3 = this.createDateTimeField(field.format.date_format);
          pass = this.isAfter({
            value: _upperBoundary2.subtract(1, 'days')
          }, val, _tempField3, timezone);
          break;
        }
      case 'is during the current':
        switch (rule.type) {
          case 'week':
            // need to use `weekYear()` instead of `year()` to handle the case where the year starts mid-week
            if (!momentVal || momentVal.week() !== momentToday.week() || momentVal.weekYear() !== momentToday.weekYear()) {
              pass = false;
            }
            break;
          case 'month':
            if (!momentVal || momentVal.month() !== momentToday.month() || momentVal.year() !== momentToday.year()) {
              pass = false;
            }
            break;
          case 'quarter':
            // eslint-disable-next-line max-len
            if (!momentVal || momentVal.quarter() !== momentToday.quarter() || momentVal.year() !== momentToday.year()) {
              pass = false;
            }
            break;
          case 'year':
            if (!momentVal || momentVal.year() !== momentToday.year()) {
              pass = false;
            }
            break;
          default:
            // TODO: Should we do anything? Throw an error? Set `pass` to false?
            break;
        }
        break;
      case 'is a day of the week':
        if (!momentVal || momentVal.day() !== Number(rule.value)) {
          pass = false;
        }
        break;

      // for days of the week, to_date and from_date are actually simple integers
      case 'is between days of the week':
        {
          if (_.isNil(momentVal)) {
            return false;
          }
          var fromDayOfWeek = momentVal.day();
          var toDate = Number(rule.to_date);
          if (rule.from_date > rule.to_date) {
            toDate += 7;
            if (fromDayOfWeek === 0) {
              fromDayOfWeek = 7;
            }
          }
          if (!momentVal || fromDayOfWeek < Number(rule.from_date) || momentVal.day() > toDate) {
            pass = false;
          }
          break;
        }
      case 'is between dates':
        if (rule.value && rule.value.to_date && rule.value.from_date) {
          // Rule to check the later of the two dates
          var beforeRule = {
            value: rule.value.to_date
          };

          // Rule to check the earlier of the two dates
          var afterRule = {
            value: rule.value.from_date
          };

          // If val > from_date and val < to_date, then we're between the two dates
          pass = this.isBefore(beforeRule, val, field, timezone) && this.isAfter(afterRule, val, field, timezone);
          break;
        }
        pass = false;
        break;
      case 'is a future date':
        if (!momentVal || momentVal.isBefore(momentToday)) {
          pass = false;
        }
        break;
      case 'is not a future date':
        if (!momentVal || momentVal.isAfter(momentToday)) {
          pass = false;
        }
        break;
      case 'size is greater than':
        // if the val for a file is a string, that means we are running on the client and have no knowledge of this file
        if (!val || typeof val === 'string') {
          pass = false;
        } else {
          var size = rule.value;
          switch (rule.unit) {
            case 'megabytes':
              size *= 1024 * 1024;
              break;
            case 'kilobytes':
              size *= 1024;
              break;
            default:
              // do nothing
              break;
          }

          // subtract 332, which is how many bytes s3 adds to each file
          if (val.size - 332 < size) {
            pass = false;
          }
        }
        break;
      case 'size is less than':
        // if the val for a file is a string, that means we are running on the client and have no knowledge of this file
        if (!val || typeof val === 'string') {
          pass = false;
        } else {
          var _size = rule.value;
          switch (rule.unit) {
            case 'megabytes':
              _size *= 1024 * 1024;
              break;
            case 'kilobytes':
              _size *= 1024;
              break;
            default:
              // do nothing
              break;
          }

          // subtract 332, which is how many bytes s3 adds to each file
          if (val.size - 332 > _size) {
            pass = false;
          }
        }
        break;
      case 'file type is':
        // if the val for a file is a string, that means we are running on the client and have no knowledge of this file
        if (!val || typeof val === 'string') {
          pass = false;
        } else if (!val.filename || val.filename.toLowerCase().indexOf(".".concat(rule.value.toLowerCase())) === -1) {
          pass = false;
        }
        break;
      case 'file type is not':
        // if the val for a file is a string, that means we are running on the client and have no knowledge of this file
        if (!val || typeof val === 'string') {
          pass = false;
        } else if (!val.filename || val.filename.toLowerCase().indexOf(".".concat(rule.value.toLowerCase())) > -1) {
          pass = false;
        }
        break;
      default:
        // TODO: rule.operator was invalid. Should we do anything? Throw an error? Set `pass` to false?
        break;
    }
    if (debugging) {
      log("condition passed: ".concat(pass));
    }
    return pass;
  },
  extractNumericValue: function extractNumericValue(originalValue, field) {
    var out = 0;
    var val = originalValue;

    // handle special case value extraction for timer fields. this allows rules to operate using
    // the same units as the originating timer field
    if (val && val.times) {
      var dates = val.times[0];
      var toDate = new Date(dates.to.date);
      toDate.setHours(this.getHours(dates.to));
      toDate.setMinutes(dates.to.minutes);
      var fromDate = new Date(dates.from.date);
      fromDate.setHours(this.getHours(dates.from));
      fromDate.setMinutes(dates.from.minutes);
      val = toDate - fromDate;
      val /= 1000;

      // convert the value into the correct unit for the timer field
      switch (_.get(field, 'format.total_format')) {
        case 'minutes':
          val /= 60;
          break;
        case 'from-to':
        case 'hours':
          val = val / 60 / 60;
          break;
        case 'days':
          val = val / 24 / 60 / 60;
          break;
        case 'weeks':
          val = val / 7 / 24 / 60 / 60;
          break;
        case 'seconds':
        default:
          // we don't need to modify val
          break;
      }
    }
    if (val && Number.isNaN(Number(val))) {
      var numVal = String(val).replace(/,/g, '').match(/[-]?[\d+.\d]+/);
      if (numVal && numVal.length) {
        out = Number(numVal[0]);
      }
    } else {
      out = Number(val);
    }
    return out;
  },
  getConnectionValueAsString: function getConnectionValueAsString(val) {
    if (!val) {
      return '';
    }
    if (Array.isArray(val)) {
      return this.getConnectionValueAsString(val[0]);
    }

    // if value is an ObjectID, return the stringified ObjectID
    // ObjectID._id returns the stringified ObjectID, whereas ObjectID.id returns non-ASCII buffer characters
    if (val._id) {
      return String(val._id);
    }

    // id refers to the record's identifier
    if (val.id) {
      return String(val.id);
    }
    return String(val);
  }
};
module.exports = commonRuleHelper;